// @flow
import React, {
  createContext,
  forwardRef,
  useContext,
  useCallback,
  useRef,
  type Context,
  type Node,
} from "react";

import { identity, memoizeWith } from "ramda";

const memoize = memoizeWith(identity);

const inVisibleScrollArea = (parent, item) => {
  const parentTop = parent.scrollTop;
  const parentBot = parentTop + parent.clientHeight;

  const itemTop = item.offsetTop;
  const itemBot = item.clientHeight + itemTop;

  return itemBot <= parentBot && itemTop >= parentTop;
};

type ScrollContextType = {
  addTarget: string => HTMLElement => mixed,
  scrollToTarget: (string, options: any) => mixed,
};
const ScrollContext: Context<ScrollContextType> = createContext({});

export const useScrollArea = () => {
  const scrollRef = useRef<?HTMLElement>(document.scrollingElement);
  const targets = useRef({});
  const addTarget: string => (?HTMLElement) => mixed = useCallback(
    memoize((name: string) => (ref: ?HTMLElement) => {
      targets.current[name] = ref;
    }),
    [targets]
  );
  const scrollToTarget = useCallback(
    (name: string, options: any = {}) => {
      const target = targets.current[name];
      if (
        target &&
        scrollRef.current &&
        !inVisibleScrollArea(scrollRef.current, target)
      ) {
        scrollRef.current.scrollTop =
          target.offsetTop + (options.offsetTop || 0);
      }
    },
    [scrollRef, targets]
  );

  const ScrollArea = useCallback(
    (props: *) => (
      <ScrollContext.Provider
        {...props}
        value={{ addTarget, scrollToTarget }}
      />
    ),
    [addTarget, scrollToTarget]
  );

  return {
    registerScrollArea: scrollRef,
    registerScrollTarget: addTarget,
    scrollTo: scrollToTarget,
    ScrollArea,
  };
};

export const ScrollArea = (props: *) => {
  const { ScrollArea } = useScrollArea();
  return <ScrollArea {...props} />;
};

export const useScrollTo = () => {
  const { scrollToTarget } = useContext(ScrollContext);
  return scrollToTarget;
};

type ScrollTargetProps = {
  as?: string,
  name: string,
  children: Node,
};
export const ScrollTarget = forwardRef<ScrollTargetProps, HTMLElement>(
  ({ as: AsComponent = "div", name, children, ...props }, ref) => {
    const { addTarget } = useContext(ScrollContext);
    const setTarget = useCallback(
      element => {
        if (typeof ref === "function") {
          ref(element);
        } else if (ref) {
          ref.current = element;
        }
        if (element) addTarget(name)(element);
      },
      [addTarget, name, ref]
    );
    return (
      <AsComponent ref={setTarget} {...props}>
        {children}
      </AsComponent>
    );
  }
);
